/* eslint-disable @typescript-eslint/no-unused-expressions */
import { RootState } from '../../app/store';
import {
  createSlice,
  PayloadAction,
  createAsyncThunk,
  createSelector,
} from '@reduxjs/toolkit';
import { getCookie, setCookie } from '../../utils/cookieHandlers';
import { SiteType } from './sitesAPI';
import { sitesApi } from './sitesAPI';
import { selectFilteredSiteList, selectSiteList } from '../auth/authSlice';
import { selectSiteId } from '../auth/authSlice';
import { selectFilteredSitesSelector } from './sitesAPI';
import { selectControllerNameInSite } from '@features/controllers/controllersSlice';

// Custom List
export type SiteFilterBy =
  | Exclude<
      keyof SiteType,
      | 'latitude'
      | 'longitude'
      | 'leo_installed'
      | 'floor_plan_enabled'
      | 'terminal_mode_enabled'
      | 'controller_backup_enabled'
      | 'ingest_data'
      | 'cloud_contacts'
      | 'cloud_users_limit'
      | 'cloud_start_date'
      | 'cloud_end_date'
      | 'active_alarm_count'
      | 'critical_active_alarm_count'
      | 'controllers'
      | 'controllers_in_alarm'
      | 'voice_sms_subscription'
      | 'sms_receivers_limit'
      | 'voip_receivers_limit'
      | 'mqtt_path'
      | 'time_zone'
      | 'store_address'
    >
  | 'All'
  | 'controller_type'
  | 'subscriptionStatus';
export type SitesState = {
  filteredSiteOperators: string[];
  filterBy: SiteFilterBy;
  filterValue: string;
};

export const getOperatorsFilterCookie = (cookieName: string) => {
  const cookieValUnparsed = getCookie(cookieName);
  if (cookieValUnparsed) {
    const hiddenOperators = cookieValUnparsed.split('~');
    return hiddenOperators;
  }

  return [];
};

const setOperatorFilterCookie = (cookieName: string, operators: string[]) => {
  setCookie(cookieName, operators.join('~'));
};

const initialState: SitesState = {
  filteredSiteOperators: [],
  filterBy: 'All',
  filterValue: '',
};

export const initFilterOperatorsThunk = createAsyncThunk<
  { username: string },
  undefined,
  { state: RootState }
>('sites/initFilterOperatorsThunk', async (_arg, { getState }) => {
  return { username: getState().auth.username };
});

export const toggleFilterOperatorThunk = createAsyncThunk<
  { operator: string; username: string },
  string,
  { state: RootState }
>('sites/toggleFilterOperatorThunk', async (operator, { getState }) => {
  return { operator, username: getState().auth.username };
});

export const setOperatorsFilterThunk = createAsyncThunk<
  { operators: string[]; username: string },
  string[],
  { state: RootState }
>('sites/setOperatorsFilterThunk', async (operators, { getState }) => {
  return { operators, username: getState().auth.username };
});

export const resetOperatorsFilterThunk = createAsyncThunk<
  { username: string },
  undefined,
  { state: RootState }
>('sites/resetOperatorsFilterThunk', async (_arg, { getState }) => {
  return { username: getState().auth.username };
});

export const sitesSlice = createSlice({
  name: 'sites',
  initialState,
  reducers: {
    toggleHideChain: (state, action: PayloadAction<string>) => {
      const hiddenChainsCopy = state.filteredSiteOperators.concat();
      if (state.filteredSiteOperators.includes(action.payload)) {
        // remove
        const index = hiddenChainsCopy.findIndex((v) => v === action.payload);
        if (index > -1) {
          hiddenChainsCopy.splice(index, 1);
          state.filteredSiteOperators = hiddenChainsCopy;
        }
      } else {
        //add
        hiddenChainsCopy.push(action.payload);
        state.filteredSiteOperators = hiddenChainsCopy;
      }
    },
    setHiddenChains: (state, action: PayloadAction<string[]>) => {
      state.filteredSiteOperators = action.payload;
    },
    resetHiddenChains: (state) => {
      state.filteredSiteOperators = [];
    },
    setSiteFilterBy: (state, action: PayloadAction<SiteFilterBy>) => {
      state.filterBy = action.payload;
    },
    setSiteFilterValue: (state, action: PayloadAction<string>) => {
      state.filterValue = action.payload;
    },
  },
  extraReducers(builder) {
    (builder.addCase(initFilterOperatorsThunk.fulfilled, (state, action) => {
      if (action.payload.username !== '') {
        const filteredOperators = getOperatorsFilterCookie(
          `hiddenChains~${action.payload.username}`,
        );
        state.filteredSiteOperators = filteredOperators;
      }
    }),
      builder.addCase(toggleFilterOperatorThunk.fulfilled, (state, action) => {
        const hiddenChainsCopy = state.filteredSiteOperators.concat();
        if (state.filteredSiteOperators.includes(action.payload.operator)) {
          // remove
          const index = hiddenChainsCopy.findIndex(
            (v) => v === action.payload.operator,
          );
          if (index > -1) {
            hiddenChainsCopy.splice(index, 1);
            state.filteredSiteOperators = hiddenChainsCopy;
          }
        } else {
          //add
          hiddenChainsCopy.push(action.payload.operator);
          state.filteredSiteOperators = hiddenChainsCopy;
        }

        if (action.payload.username !== '') {
          setOperatorFilterCookie(
            `hiddenChains~${action.payload.username}`,
            hiddenChainsCopy,
          );
        }
      }),
      builder.addCase(setOperatorsFilterThunk.fulfilled, (state, action) => {
        if (action.payload.username !== '') {
          setOperatorFilterCookie(
            `hiddenChains~${action.payload.username}`,
            action.payload.operators,
          );
        }
        state.filteredSiteOperators = action.payload.operators;
      }),
      builder.addCase(resetOperatorsFilterThunk.fulfilled, (state, action) => {
        if (action.payload.username !== '') {
          setOperatorFilterCookie(
            `hiddenChains~${action.payload.username}`,
            [],
          );
        }
        state.filteredSiteOperators = [];
      }));
  },
});

export const {
  toggleHideChain,
  setHiddenChains,
  resetHiddenChains,
  setSiteFilterBy,
  setSiteFilterValue,
} = sitesSlice.actions;

export const selectFilteredSiteOperators = (state: RootState) =>
  state.sites.filteredSiteOperators;
export const selectSiteFilterBy = (state: RootState) => state.sites.filterBy;
export const selectSiteFilterValue = (state: RootState) =>
  state.sites.filterValue;

export default sitesSlice.reducer;

export const selectSiteData = sitesApi.endpoints.getSites.select;

const makeSelectStoreListArg = createSelector(
  [selectSiteList],
  (store_list) => {
    return { store_list };
  },
);

export const selectAllSitesData = createSelector(
  [(state: RootState) => state, makeSelectStoreListArg],
  (state, queryArg) => {
    return sitesApi.endpoints.getSites.select(queryArg)(state);
  },
);

export const selectSite = createSelector(
  [
    selectSiteId,
    (state: RootState) => selectAllSitesData(state).data,
    (state: RootState) => selectAllSitesData(state).isLoading,
    // selectAllSitesData,
  ],
  (siteId, data, isLoading) => {
    if (data?.length) {
      const site = data.find((s) => s.store_num === siteId);
      return { site, isLoading };
    }
    return { site: undefined, isLoading };
  },
);

export const selectSiteControllerTypeFromName = createSelector(
  [selectControllerNameInSite, (state: RootState) => state],
  ({ controllerName, siteId }, state) => {
    const { site } = selectSite(state, siteId);

    if (!site) {
      return undefined;
    }

    const controllerType = site.controllers?.find(
      (controller) => controller.controller_name === controllerName,
    )?.controller_type;

    return controllerType;
  },
);

export const selectSiteTimezone = createSelector([selectSite], (siteData) => {
  if (siteData.site && siteData.site.time_zone) {
    return siteData.site.time_zone;
  }
  return undefined;
});

// export const selectSiteTimezone = createSelector(
//   [selectSiteList, selectSiteId, (state: RootState) => state],
//   (siteList, siteId, state) => {
//     const siteData =
//       selectSiteData({ store_list: siteList })(state)?.data ?? [];

//     if (siteData && siteData?.length) {
//       const site = siteData.find((s) => s.store_num === siteId);
//       if (site?.time_zone) return site.time_zone;
//     }
//     return undefined;
//   },
// );

export const selectSitesWithVoiceSms = createSelector(
  [selectSiteList, selectFilteredSiteList, (state: RootState) => state],
  (siteList, filteredSiteList, state) => {
    const siteData =
      selectSiteData({ store_list: siteList })(state)?.data ?? [];

    if (siteData && siteData?.length) {
      const storesWithVoiceSms = selectFilteredSitesSelector(
        { data: siteData },
        filteredSiteList,
      ).filter((store) => store.voice_sms_subscription !== null);
      return storesWithVoiceSms;
    }

    return undefined;
  },
);

export const selectSitesWithControllerBackups = createSelector(
  [selectSiteList, selectFilteredSiteList, (state: RootState) => state],
  (siteList, filteredSiteList, state) => {
    const siteData =
      selectSiteData({ store_list: siteList })(state)?.data ?? [];

    if (siteData && siteData?.length) {
      const storesWithControllerBackups = selectFilteredSitesSelector(
        { data: siteData },
        filteredSiteList,
      ).filter((store) => store.controller_backup_enabled === true);
      return storesWithControllerBackups;
    }

    return undefined;
  },
);

export const selectSiteRefrigerantTrackingEnabled = createSelector(
  [selectSite],
  ({ site }) => {
    return site?.refrigerant_tracking_enabled;
  },
);
